import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@heroui/react';
import { Input } from '@heroui/input';
import { Select, SelectItem } from '@/components/ui/AppSelect';
import Plus from '@/icons/Plus';
import Trash from '@/icons/Trash';
import Edit from '@/icons/Edit';
import {
    getPricingMembers,
    createPricingMember,
    updatePricingMember,
    deletePricingMember,
    bulkImportPricingMembers,
} from '@/apiEndpoints';
import { sectionTitle, bodyMuted, card, label as labelCls } from '@/components/ui/design';
import type { PricingMember, PricingMemberInput } from '@/apiEndpoints';

const inputClassNames = {
    label: 'text-grey40 dark:text-nightMuted',
    input: 'text-base text-grey10 dark:text-white placeholder:text-grey60 dark:placeholder:text-nightMuted',
    inputWrapper: 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.08] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
};

const selectClassNames = {
    label: 'text-grey40 dark:text-nightMuted',
    value: 'text-grey10 dark:text-white',
    trigger: 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.08]',
    popoverContent: 'rounded-2xl bg-white dark:bg-nightElevated text-grey10 dark:text-nightTextSecondary',
};

const iconBtn = 'inline-flex h-9 w-9 items-center justify-center rounded-full text-grey30 ring-1 ring-black/[0.06] transition-colors hover:bg-grey99 dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/[0.06]';
const iconBtnDanger = 'inline-flex h-9 w-9 items-center justify-center rounded-full text-grey30 ring-1 ring-black/[0.06] transition-colors hover:bg-red-500/10 hover:text-red-600 dark:text-nightTextSecondary dark:ring-white/10 dark:hover:text-red-400';
const typePill = 'inline-flex max-w-full truncate rounded-full bg-black/[0.05] px-2.5 py-0.5 text-xs font-semibold text-grey30 dark:bg-white/10 dark:text-nightTextSecondary';
const modalClassNames = 'mx-4 rounded-[28px] bg-white dark:bg-nightElevated ring-1 ring-black/[0.06] dark:ring-white/10';

interface MembersTabProps {
    organizerId: string | null;
}

const EMPTY_FORM: PricingMemberInput = {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    type: '',
};

function parseCSVLine(line: string): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (inQuotes) {
            if (char === '"' && line[i + 1] === '"') {
                current += '"';
                i++;
            } else if (char === '"') {
                inQuotes = false;
            } else {
                current += char;
            }
        } else if (char === '"') {
            inQuotes = true;
        } else if (char === ',') {
            values.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    values.push(current.trim());
    return values;
}

function parseMembersCsv(csvText: string): PricingMemberInput[] {
    const lines = csvText.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length < 2) return [];

    const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
    const idx = {
        firstName: headers.findIndex(h => h.includes('first')),
        lastName: headers.findIndex(h => h.includes('last')),
        email: headers.findIndex(h => h.includes('email')),
        phone: headers.findIndex(h => h.includes('phone')),
        address: headers.findIndex(h => h.includes('address')),
        type: headers.findIndex(h => h.includes('type')),
    };

    return lines.slice(1).map(line => {
        const values = parseCSVLine(line);
        return {
            firstName: idx.firstName >= 0 ? values[idx.firstName] || '' : '',
            lastName: idx.lastName >= 0 ? values[idx.lastName] || '' : '',
            email: idx.email >= 0 ? values[idx.email] || '' : '',
            phone: idx.phone >= 0 ? values[idx.phone] || '' : '',
            address: idx.address >= 0 ? values[idx.address] || '' : '',
            type: idx.type >= 0 ? values[idx.type] || '' : '',
        };
    }).filter(m => m.email);
}

export default function MembersTab({ organizerId }: MembersTabProps) {
    const { isOpen: isFormOpen, onOpen: onFormOpen, onOpenChange: onFormOpenChange } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onOpenChange: onDeleteOpenChange } = useDisclosure();
    const [editingMember, setEditingMember] = useState<PricingMember | null>(null);
    const [deletingMember, setDeletingMember] = useState<PricingMember | null>(null);
    const [formData, setFormData] = useState<PricingMemberInput>(EMPTY_FORM);
    const [importError, setImportError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const queryClient = useQueryClient();

    const { data: members = [], isLoading, isError } = useQuery({
        queryKey: ['pricingMembers', organizerId],
        queryFn: () => getPricingMembers(organizerId as string),
        enabled: !!organizerId,
    });

    const memberTypes = useMemo(() => {
        const types = new Set<string>();
        members.forEach((m) => {
            if (m.type && m.type.trim()) types.add(m.type.trim());
        });
        return Array.from(types).sort();
    }, [members]);

    const filteredMembers = useMemo(() => {
        let result = members;
        if (typeFilter === '__none__') {
            result = result.filter((m) => !m.type || !m.type.trim());
        } else if (typeFilter !== 'all') {
            result = result.filter((m) => m.type === typeFilter);
        }

        const query = search.trim().toLowerCase();
        if (query) {
            result = result.filter((m) => {
                const fullName = `${m.firstName || ''} ${m.lastName || ''}`.toLowerCase();
                return fullName.includes(query) || m.email.toLowerCase().includes(query);
            });
        }

        return result;
    }, [members, typeFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filteredMembers.length / pageSize));
    const pagedMembers = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredMembers.slice(start, start + pageSize);
    }, [filteredMembers, page]);

    // Keep the current page in range whenever the filter or underlying data changes
    useEffect(() => {
        setPage((p) => Math.min(p, totalPages));
    }, [totalPages]);

    const createMutation = useMutation({
        mutationFn: (data: PricingMemberInput) => createPricingMember(organizerId as string, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricingMembers', organizerId] });
            resetForm();
            onFormOpenChange();
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: PricingMemberInput }) => updatePricingMember(organizerId as string, id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricingMembers', organizerId] });
            resetForm();
            setEditingMember(null);
            onFormOpenChange();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deletePricingMember(organizerId as string, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricingMembers', organizerId] });
            onDeleteOpenChange();
        },
    });

    const bulkImportMutation = useMutation({
        mutationFn: (members: PricingMemberInput[]) => bulkImportPricingMembers(organizerId as string, members),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricingMembers', organizerId] });
        },
    });

    const resetForm = () => setFormData(EMPTY_FORM);

    const submitForm = () => {
        if (!formData.email.trim()) return;
        if (editingMember) {
            updateMutation.mutate({ id: editingMember._id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const handleAdd = () => {
        resetForm();
        setEditingMember(null);
        onFormOpen();
    };

    const handleEdit = (member: PricingMember) => {
        setEditingMember(member);
        setFormData({
            email: member.email,
            firstName: member.firstName || '',
            lastName: member.lastName || '',
            phone: member.phone || '',
            address: member.address || '',
            type: member.type || '',
        });
        onFormOpen();
    };

    const handleDelete = (member: PricingMember) => {
        setDeletingMember(member);
        onDeleteOpen();
    };

    const confirmDelete = () => {
        if (deletingMember) {
            deleteMutation.mutate(deletingMember._id);
        }
    };

    const handleDownloadSampleCsv = () => {
        const header = 'firstName,lastName,email,phone,address,type';
        const sampleRow = 'Jane,Doe,jane.doe@example.com,555-123-4567,"123 Main St, Springfield",VIP';
        const csvContent = `${header}\n${sampleRow}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'members-sample.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleImportClick = () => {
        setImportError(null);
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const parsed = parseMembersCsv(text);
            if (parsed.length === 0) {
                setImportError('No valid rows found. Make sure the CSV has an "email" column.');
                return;
            }
            bulkImportMutation.mutate(parsed);
        } catch (error) {
            setImportError('Failed to read the CSV file.');
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    if (!organizerId) {
        return <div className={`text-center py-16 ${bodyMuted}`}>Select an organization to manage its members.</div>;
    }

    if (isLoading) return <div className={`text-center py-16 ${bodyMuted}`}>Loading members...</div>;
    if (isError) return <div className="text-center py-16 text-red-500 dark:text-red-400">Error loading members</div>;

    const labelClass = labelCls;
    const outlineButtonClass = 'no-global-button-color h-11 rounded-full bg-transparent px-5 ring-1 ring-grey90 text-grey10 dark:ring-white/15 dark:!text-white text-sm font-semibold data-[hover=true]:bg-grey99 dark:data-[hover=true]:bg-white/[0.06]';
    const primaryButtonClass = 'no-global-button-color h-11 rounded-full bg-grey10 px-6 text-white text-sm font-semibold data-[hover=true]:bg-black dark:bg-white dark:!text-nightBg dark:data-[hover=true]:bg-grey90';

    return (
        <div className="pt-0 pb-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between mb-3">
                <h2 className={sectionTitle}>Members</h2>
                <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap">
                    <Button className={outlineButtonClass} onClick={handleDownloadSampleCsv}>
                        Sample CSV
                    </Button>
                    <Button className={outlineButtonClass} onClick={handleImportClick} isLoading={bulkImportMutation.isPending}>
                        Import CSV
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                    <Button
                        onClick={handleAdd}
                        className="col-span-2 md:col-span-1 no-global-button-color h-11 rounded-full bg-primary px-6 text-white text-sm font-semibold data-[hover=true]:bg-primary30 dark:bg-nightAccent dark:!text-nightBg dark:data-[hover=true]:bg-primary70"
                        startContent={<Plus className="text-white dark:text-nightBg" color="currentColor" />}
                    >
                        Add Member
                    </Button>
                </div>
            </div>
            <p className={`max-w-[640px] mb-6 ${bodyMuted}`}>
                Members added here automatically qualify for a ticket's member pricing discount when they check out with a matching email.
            </p>

            <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-6">
                <div className="w-full sm:max-w-xs">
                    <label className={labelClass}>Search</label>
                    <Input
                        className="w-full"
                        aria-label="Search"
                        placeholder="Search by name or email"
                        value={search}
                        classNames={{ ...inputClassNames, inputWrapper: `${inputClassNames.inputWrapper} !h-14 !min-h-14` }}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className="w-full sm:max-w-xs">
                    <label className={labelClass}>Filter by type</label>
                    <Select
                        className="w-full"
                        aria-label="Filter by type"
                        selectedKeys={[typeFilter]}
                        classNames={selectClassNames}
                        onSelectionChange={(keys) => {
                            const value = Array.from(keys)[0] as string;
                            setTypeFilter(value || 'all');
                            setPage(1);
                        }}
                        isDisabled={memberTypes.length === 0}
                    >
                        {[
                            <SelectItem key="all">All Types</SelectItem>,
                            <SelectItem key="__none__">No Type</SelectItem>,
                            ...memberTypes.map((t) => <SelectItem key={t}>{t}</SelectItem>),
                        ]}
                    </Select>
                </div>
            </div>

            {importError && (
                <div className="inline-flex rounded-full bg-red-500/10 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 mb-4">{importError}</div>
            )}
            {bulkImportMutation.isSuccess && (
                <div className="inline-flex rounded-full bg-green-500/10 px-4 py-2 text-sm font-medium text-green-700 dark:text-green-400 mb-4">
                    Imported {bulkImportMutation.data.created} member(s){bulkImportMutation.data.skipped > 0 ? `, skipped ${bulkImportMutation.data.skipped} duplicate(s)` : ''}.
                </div>
            )}

            {/* Add/Edit Modal */}
            <Modal
                isOpen={isFormOpen}
                onOpenChange={onFormOpenChange}
                size="2xl"
                placement="center"
                scrollBehavior="inside"
                classNames={{ base: modalClassNames, header: 'text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white', closeButton: 'text-grey10 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10' }}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                {editingMember ? 'Edit Member' : 'Add Member'}
                            </ModalHeader>
                            <ModalBody>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Email *</label>
                                        <Input
                                            type="email"
                                            value={formData.email}
                                            classNames={inputClassNames}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="member@example.com"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Type</label>
                                        <Input
                                            value={formData.type}
                                            classNames={inputClassNames}
                                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                            placeholder="e.g., VIP, Board Member"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>First Name</label>
                                        <Input
                                            value={formData.firstName}
                                            classNames={inputClassNames}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                            placeholder="Jane"
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Last Name</label>
                                        <Input
                                            value={formData.lastName}
                                            classNames={inputClassNames}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                            placeholder="Doe"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Phone</label>
                                        <Input
                                            value={formData.phone}
                                            classNames={inputClassNames}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            placeholder="555-123-4567"
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Address</label>
                                        <Input
                                            value={formData.address}
                                            classNames={inputClassNames}
                                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                            placeholder="123 Main St"
                                        />
                                    </div>
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button className={outlineButtonClass} onPress={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    className={primaryButtonClass}
                                    onPress={submitForm}
                                    isLoading={createMutation.isPending || updateMutation.isPending}
                                    isDisabled={!formData.email.trim()}
                                >
                                    {editingMember ? 'Update' : 'Add'}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteOpen}
                onOpenChange={onDeleteOpenChange}
                placement="center"
                classNames={{ base: modalClassNames, header: 'text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white', body: 'text-grey30 dark:text-nightTextSecondary', closeButton: 'text-grey10 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10' }}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">Delete Member</ModalHeader>
                            <ModalBody>
                                <p className="break-words">Are you sure you want to remove <strong>{deletingMember?.email}</strong> from the members list?</p>
                            </ModalBody>
                            <ModalFooter>
                                <Button className={outlineButtonClass} onPress={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    color="danger"
                                    className="no-global-button-color h-11 rounded-full bg-red-500 px-6 text-white text-sm font-semibold dark:!text-white"
                                    onPress={confirmDelete}
                                    isLoading={deleteMutation.isPending}
                                >
                                    Delete
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Members List */}
            <div className="space-y-4">
                {filteredMembers.length === 0 ? (
                    <div className="rounded-[28px] bg-grey99 dark:bg-white/[0.04] px-6 py-16 text-center text-base text-grey40 dark:text-nightMuted">
                        {members.length === 0
                            ? 'No members added yet. Add your first member or import a CSV to get started.'
                            : 'No members match this filter.'}
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className={`hidden md:block overflow-hidden ${card}`}>
                            <div className="grid grid-cols-[1fr_1fr_1.6fr_1.2fr_1fr_auto] gap-4 py-4 px-6 bg-grey99 dark:bg-white/[0.03] text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">
                                <div>First Name</div>
                                <div>Last Name</div>
                                <div>Email</div>
                                <div>Phone</div>
                                <div>Type</div>
                                <div>Actions</div>
                            </div>
                            <div className="divide-y divide-black/[0.06] dark:divide-white/[0.08]">
                                {pagedMembers.map((member) => (
                                    <div key={member._id} className="grid grid-cols-[1fr_1fr_1.6fr_1.2fr_1fr_auto] gap-4 py-4 px-6 transition-colors hover:bg-grey99/60 dark:hover:bg-white/[0.02]">
                                        <div className="flex items-center min-w-0 truncate text-sm text-grey30 dark:text-nightTextSecondary" title={member.firstName || ''}>{member.firstName || '-'}</div>
                                        <div className="flex items-center min-w-0 truncate text-sm text-grey30 dark:text-nightTextSecondary" title={member.lastName || ''}>{member.lastName || '-'}</div>
                                        <div className="flex items-center min-w-0 truncate text-sm font-semibold text-grey10 dark:text-white" title={member.email}>{member.email}</div>
                                        <div className="flex items-center min-w-0 truncate text-sm text-grey30 dark:text-nightTextSecondary" title={member.phone || ''}>{member.phone || '-'}</div>
                                        <div className="flex items-center min-w-0 text-sm text-grey30 dark:text-nightTextSecondary" title={member.type || ''}>{member.type ? <span className={typePill}>{member.type}</span> : '-'}</div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEdit(member)}
                                                className={iconBtn}
                                                title="Edit member"
                                            >
                                                <Edit width={16} height={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(member)}
                                                className={iconBtnDanger}
                                                title="Delete member"
                                            >
                                                <Trash width={16} height={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden flex flex-col gap-3">
                            {pagedMembers.map((member) => {
                                const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
                                return (
                                    <div key={member._id} className={`${card} p-5`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white truncate">{fullName || member.email}</div>
                                                {fullName && <div className="text-sm text-grey40 dark:text-nightMuted truncate mt-0.5">{member.email}</div>}
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <button
                                                    onClick={() => handleEdit(member)}
                                                    className={iconBtn}
                                                    title="Edit member"
                                                >
                                                    <Edit width={18} height={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(member)}
                                                    className={iconBtnDanger}
                                                    title="Delete member"
                                                >
                                                    <Trash width={18} height={18} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-black/[0.06] dark:border-white/[0.08]">
                                            <div className="min-w-0">
                                                <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Phone</div>
                                                <div className="mt-1 text-sm text-grey10 dark:text-nightTextSecondary truncate">{member.phone || '-'}</div>
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Type</div>
                                                <div className="mt-1 text-sm text-grey10 dark:text-nightTextSecondary truncate">{member.type ? <span className={typePill}>{member.type}</span> : '-'}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {totalPages > 1 && (
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
                                <span className="text-sm text-grey40 dark:text-nightMuted">
                                    Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredMembers.length)} of {filteredMembers.length}
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        className={outlineButtonClass}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        isDisabled={page === 1}
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-sm font-semibold text-grey10 dark:text-white">
                                        Page {page} of {totalPages}
                                    </span>
                                    <Button
                                        size="sm"
                                        className={outlineButtonClass}
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        isDisabled={page === totalPages}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
